import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

const CATEGORIES = [
  { v: "raw_material", l: "Raw Material" },
  { v: "packaging", l: "Packaging" },
  { v: "consumables", l: "Consumables" },
  { v: "finished_goods", l: "Finished Goods" },
  { v: "semi_finished", l: "Semi-Finished" },
];
const STATUSES = ["draft", "approved", "blocked", "discontinued", "archived"];
const RISKS = ["low", "medium", "high"];

const blank = {
  name: "", category: "raw_material", uom: "kg", hsn: "", gst_rate: 0,
  allergen: "", storage_location: "", storage_conditions: "", shelf_life_days: 0,
  min_stock: 0, max_stock: 0, reorder_level: 0, preferred_vendor: "",
  food_safety_risk: "low", barcode: "", status: "approved", notes: "",
};

export default function ItemFormDialog({ open, editing, onClose, onSubmit }) {
  const [form, setForm] = useState(blank);

  useEffect(() => {
    setForm(editing ? { ...blank, ...editing } : blank);
  }, [editing, open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      gst_rate: Number(form.gst_rate || 0),
      shelf_life_days: Number(form.shelf_life_days || 0),
      min_stock: Number(form.min_stock || 0),
      max_stock: Number(form.max_stock || 0),
      reorder_level: Number(form.reorder_level || 0),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid="item-dialog">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">Inventory</div>
            <h2 className="font-heading text-lg font-bold text-slate-900">
              {editing ? "Edit Item" : "New Item"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-slate-500 hover:bg-slate-100 rounded" data-testid="item-dialog-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <Row label="Name *">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="item-form-name" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Category">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="item-form-category">
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Row>
            <Row label="Unit of Measurement">
              <input value={form.uom} onChange={(e) => set("uom", e.target.value)} placeholder="kg, ltr, pcs…" className={inp} data-testid="item-form-uom" />
            </Row>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Row label="HSN"><input value={form.hsn} onChange={(e) => set("hsn", e.target.value)} className={inp} /></Row>
            <Row label="GST %"><input type="number" step="0.01" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={inp} /></Row>
            <Row label="Shelf Life (days)"><input type="number" value={form.shelf_life_days} onChange={(e) => set("shelf_life_days", e.target.value)} className={inp} /></Row>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Row label="Min Stock"><input type="number" step="0.001" value={form.min_stock} onChange={(e) => set("min_stock", e.target.value)} className={inp} data-testid="item-form-min" /></Row>
            <Row label="Max Stock"><input type="number" step="0.001" value={form.max_stock} onChange={(e) => set("max_stock", e.target.value)} className={inp} /></Row>
            <Row label="Reorder Level"><input type="number" step="0.001" value={form.reorder_level} onChange={(e) => set("reorder_level", e.target.value)} className={inp} data-testid="item-form-reorder" /></Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Storage Location"><input value={form.storage_location} onChange={(e) => set("storage_location", e.target.value)} placeholder="MAIN / COLD-1…" className={inp} /></Row>
            <Row label="Storage Conditions"><input value={form.storage_conditions} onChange={(e) => set("storage_conditions", e.target.value)} placeholder="Ambient / 4°C…" className={inp} /></Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Allergen"><input value={form.allergen} onChange={(e) => set("allergen", e.target.value)} placeholder="Gluten / Dairy / Nuts" className={inp} /></Row>
            <Row label="Food Safety Risk">
              <select value={form.food_safety_risk} onChange={(e) => set("food_safety_risk", e.target.value)} className={inp}>
                {RISKS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Barcode"><input value={form.barcode} onChange={(e) => set("barcode", e.target.value)} className={inp} /></Row>
            <Row label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inp}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Row>
          </div>
          <Row label="Notes">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inp} />
          </Row>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50" data-testid="item-form-cancel">Cancel</button>
          <button type="submit" className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700" data-testid="item-form-submit">
            {editing ? "Save Changes" : "Create Item"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
